import React, { useEffect, useRef, useId } from 'react';
import { IProps } from './Base64HtmlRenderer.interface';
import { decodeBase64Utf8 } from '@/utils/textDecode';
import { cleanHTML } from '@/utils/sanitizeHTML';

/**
 * Base64HtmlRenderer safely renders HTML content that may contain scripts.
 *
 * Features:
 * - Handles both base64-encoded and plain HTML content
 * - SSR-safe: Renders full HTML on server for SEO
 * - Script execution: Converts non-executable scripts to executable ones on client
 * - Order preservation: Maintains exact order of all HTML elements and scripts
 * - Sanitization: Uses cleanHTML to remove XSS vulnerabilities while preserving safe content
 *
 * How it works:
 * 1. SSR: Renders complete HTML (including scripts) via dangerouslySetInnerHTML
 * 2. Hydration: React matches server HTML on client
 * 3. useEffect: Replaces non-executable script tags with executable ones
 * 4. Cleanup: Removes created scripts on component unmount
 *
 * @example
 * // Dynamics Form
 * <Base64HtmlRenderer
 *   content="<div data-form-id='...'></div><script src='FormLoader.js'></script>"
 *   allowScripts={true}
 * />
 */
export const Base64HtmlRenderer: React.FC<IProps> = ({
    content,
    base64Encoded = false,
    className,
}) => {
    const html = base64Encoded ? decodeBase64Utf8(content) : content;
    const sanitizedHTML = cleanHTML(html, true);

    const containerRef = useRef<HTMLDivElement>(null);
    const componentId = useId();

    useEffect(() => {
        // Only run on client-side
        if (typeof window === 'undefined' || !containerRef.current) return;

        const container = containerRef.current;

        // First, remove ALL existing scripts (both processed and unprocessed)
        // This ensures a clean slate on every useEffect run
        const allExistingScripts = Array.from(container.querySelectorAll('script'));
        allExistingScripts.forEach(script => script.remove());

        // Re-render the HTML to get fresh script tags from dangerouslySetInnerHTML
        container.innerHTML = sanitizedHTML;

        // Track created script elements for cleanup
        const createdScripts: HTMLScriptElement[] = [];

        // Now find all script elements that were just rendered
        const newScripts = Array.from(container.querySelectorAll('script'));

        newScripts.forEach((oldScript, index) => {
            // Create a new executable script element
            const scriptId = `${componentId}-script-${index}`;

            const newScript = document.createElement('script');
            newScript.id = scriptId;

            // Copy all attributes from the old script
            Array.from(oldScript.attributes).forEach(attr => {
                if (attr.name !== 'id') {
                    newScript.setAttribute(attr.name, attr.value);
                }
            });

            // Copy inline script content (for inline scripts without src)
            if (oldScript.textContent && !oldScript.getAttribute('src')) {
                newScript.textContent = oldScript.textContent;
            }

            // Insert the new executable script in place of the old one
            oldScript.parentNode?.insertBefore(newScript, oldScript);

            // Remove the old non-executable script
            oldScript.remove();

            createdScripts.push(newScript);
        });

        // Cleanup function - remove created scripts when component unmounts
        return () => {
            createdScripts.forEach(script => {
                if (script.parentNode) {
                    script.parentNode.removeChild(script);
                }
            });
        };
    }, [sanitizedHTML, componentId]);

    return (
        <div
            ref={containerRef}
            className={className}
            dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
        />
    );
};